import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import {
  Alert,
  Box,
  FormControlLabel,
  IconButton,
  Snackbar,
  SnackbarCloseReason,
  Switch,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { ChangeEvent, useEffect, useState } from "react";
import { config } from "../../config";
import ComboBoxMultiQuery from "./ComboBoxMultiQuery";
import { DataProvider, OverPassTurbo } from "./DataProvider";
import LoadingButton from "./LoadingButton";
import QueryFilterArea from "./QueryFilterArea";
import DialogComponent from "./DialogComponent";

interface OverpassProps {
  map?: Map;
  view?: MapView;
  onLoading: (loading: boolean) => void;
  startSearch: boolean;
}

const getRandomNumber = (max: number) => {
  return Math.floor(Math.random() * (max - 1));
};

export const OverPass: React.FC<OverpassProps> = ({
  map,
  view,
  onLoading,
  startSearch,
}) => {
  const [selectedQueryFromUrl, setSelectedQueryFromUrl] = useState<any[]>(
    () => {
      const params = new URLSearchParams(window.location.search);
      const value = params.get("value");
      if (!value) return [];

      try {
        const parseValue = new Function("return " + value)();
        return JSON.parse(JSON.stringify(parseValue));
      } catch (error) {
        console.log("Wrong filter value");
        return [];
      }
    }
  );
  const [number, setNumber] = useState<number>(
    getRandomNumber(config.QUERY.buildInQueryFilter.length)
  );
  const [selectedQuery, setSelectedQuery] = useState<any[]>(() => {
    return selectedQueryFromUrl.length > 0
      ? selectedQueryFromUrl
      : [config.QUERY.buildInQueryFilter[number]];
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [controller, setController] = useState<AbortController | null>(null);
  const [isUnique, setIsUnique] = useState(true);
  const [errorAlert, setErrorAlert] = useState<string | undefined>(undefined);
  const [queryAreaFilter, setQueryAreaFilter] = useState<string>("");
  const [openSnackBar, setOpenSnackBar] = useState<boolean>(false);
  const [snackBarMassage, setSnackBarMessage] = useState<string>("");
  const [dialogType, setDialogType] = useState<
    "largeData" | "emptyFilter" | "queryError"
  >("largeData");
  const [count, setCount] = useState<number>(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalPromise, setModalPromise] = useState<
    ((value: boolean) => void) | null
  >(null);

  const showDecisionDialog = () => {
    setModalOpen(true);
    return new Promise((resolve) => {
      setModalPromise(() => resolve);
    });
  };

  useEffect(() => {
    onLoading(loading);
  }, [loading]);

  const handleCloseSnackBar = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSnackBar(false);
  };

  useEffect(() => {
    if (!startSearch) return;
    if (startSearch === undefined) return;
    addDataFromOverPass();
  }, [startSearch]);

  const decisionFn = async (items?: OverPassTurbo): Promise<boolean> => {
    if (items && items.elements?.length > 3000) {
      setDialogType("largeData");
      const decision: boolean = (await showDecisionDialog()) as boolean;
      setModalOpen(false);
      return decision;
    }
    return true;
  };

  const addDataFromOverPass = async () => {
    //const url = `https://overpass-api.de/api/interpreter?data=[out:json][timeout:25];nwr["tourism"="hotel"](${bbox});out geom;`;
    if (!queryAreaFilter) return;
    if (!selectedQuery.length) {
      setDialogType("emptyFilter");

      const decision: boolean = (await showDecisionDialog()) as boolean;
      setModalOpen(false);

      if (!decision) {
        setLoading(false);
        return;
      }
      return;
    }
    setLoading(true);

    const queryObject = selectedQuery
      .map((e) => `${e.key}${queryAreaFilter}`)
      .join(";");

    const query = `[out:json][timeout:95];${queryObject};out geom;`;
    const dp = new DataProvider("overpass-turbo");

    const newController = new AbortController();
    setController(newController);

    const { items, error } = await dp.getData(query, newController.signal);

    if (!items) {
      setDialogType("queryError");
      setLoading(false);
      const decision: boolean = (await showDecisionDialog()) as boolean;
      setModalOpen(false);
      return;
    }

    setCount(items?.elements.length || 0);

    const decis = await decisionFn(items);

    if (!decis) {
      setLoading(false);
      return;
    }

    const data = dp.parseDataOverpassTurbo(items);

    if (error) {
      setErrorAlert(error);
      setLoading(false);
      return;
    } else {
      setErrorAlert(undefined);
    }

    if (!map) return;
    const graphicLayer = map.findLayerById("draw");
    if (!(graphicLayer instanceof GraphicsLayer)) return;

    if (isUnique) {
      const idnodes = graphicLayer.graphics.map((e) => e.attributes.idnode);
      const filterGraphicsData = data?.filter(
        (graphic: { attributes: { idnode: any } }) =>
          !idnodes.includes(graphic.attributes.idnode)
      );
      graphicLayer.addMany(filterGraphicsData as Graphic[]);
    } else {
      graphicLayer.addMany(data as Graphic[]);
    }

    setLoading(false);

    data?.length !== undefined && setOpenSnackBar(true);
    data?.length !== undefined && setSnackBarMessage(`Find ${data?.length}`);
  };

  const handleUniqueChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsUnique(event.target.checked);
  };

  return (
    <>
      <Snackbar
        open={openSnackBar}
        autoHideDuration={5000}
        onClose={handleCloseSnackBar}
        message={snackBarMassage}
      />
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={1}>
          {errorAlert && (
            <Grid size={{ xs: 12, md: 12 }}>
              <Alert
                severity="warning"
                onClose={() => {
                  setErrorAlert(undefined);
                }}
              >
                {errorAlert}
              </Alert>
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 12 }}>
            <ComboBoxMultiQuery
              queryFilter={config.QUERY.buildInQueryFilter}
              defaultValue={selectedQuery}
              selectedValues={(e) => setSelectedQuery(e)}
            ></ComboBoxMultiQuery>
          </Grid>
          <QueryFilterArea
            view={view}
            map={map}
            queryAreaFilter={(filterArea) => {
              setQueryAreaFilter(filterArea);
            }}
          ></QueryFilterArea>
          <Grid size={{ xs: 12, md: 12 }}>
            <FormControlLabel
              style={{ marginLeft: "10px" }}
              control={
                <Switch
                  defaultChecked
                  size="small"
                  onChange={handleUniqueChange}
                />
              }
              label="Unique data"
              title="Add to map only unique data"
            />
          </Grid>

          <Grid size={{ xs: 1, md: 1 }}></Grid>
          <Grid size={{ xs: 7, md: 7 }}>
            <LoadingButton
              loading={loading}
              onClickAction={addDataFromOverPass}
            ></LoadingButton>
          </Grid>
          <Grid size={{ xs: 2, md: 2 }}>
            {loading && (
              <IconButton
                title="Stop execute query"
                aria-label="stop query"
                color="primary"
                onClick={() => {
                  if (!controller) return;
                  controller.abort();
                  setLoading(false);
                }}
              >
                <CancelPresentationIcon />
              </IconButton>
            )}
          </Grid>
        </Grid>
      </Box>
      {modalOpen && (
        <DialogComponent
          count={count}
          isOpen={modalOpen}
          resolve={modalPromise}
          type={dialogType}
        />
      )}
    </>
  );
};

export default OverPass;
